// Business-hours arithmetic for the frontend.
//
// Mirrors helpdesk-functions/functions/src/lib/businessHours.ts so the
// SLA countdowns can show "business minutes remaining" without a
// roundtrip. Pure functions, no deps — keep the two copies in sync.

import { BusinessSchedule } from "@/types";

interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    weekday: "short",
  });
}

function localParts(date: Date, timeZone: string): LocalParts {
  const parts = partsFormatter(timeZone).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  let hour = Number(get("hour"));
  if (hour === 24) hour = 0;
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour,
    minute: Number(get("minute")),
    second: Number(get("second")),
    weekday: WEEKDAY_INDEX[get("weekday")] ?? 0,
  };
}

function tzOffsetMs(date: Date, timeZone: string): number {
  const p = localParts(date, timeZone);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asIfUtc - date.getTime();
}

function fromLocalParts(
  year: number, month: number, day: number,
  hour: number, minute: number, second: number,
  timeZone: string,
): Date {
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const offset = tzOffsetMs(new Date(naiveUtc), timeZone);
  return new Date(naiveUtc - offset);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function dateKey(p: LocalParts): string {
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

function parseHM(hm: string): [number, number] {
  const [h, m] = hm.split(":").map((s) => Number(s));
  return [Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0];
}

function nextOrCurrentWindow(
  after: Date,
  schedule: BusinessSchedule,
): { start: Date; end: Date } {
  const { timezone, workingDays, holidays, workingHours } = schedule;
  const tz = timezone || "UTC";
  const [startH, startM] = parseHM(workingHours.start);
  const [endH, endM] = parseHM(workingHours.end);
  const workingDaySet = new Set(workingDays);
  const holidaySet = new Set(holidays);

  let cursor = after;
  for (let i = 0; i < 366; i++) {
    const p = localParts(cursor, tz);
    if (workingDaySet.has(p.weekday) && !holidaySet.has(dateKey(p))) {
      const winStart = fromLocalParts(p.year, p.month, p.day, startH, startM, 0, tz);
      const winEnd = fromLocalParts(p.year, p.month, p.day, endH, endM, 0, tz);
      if (winEnd.getTime() > after.getTime()) {
        return { start: winStart, end: winEnd };
      }
    }
    cursor = fromLocalParts(p.year, p.month, p.day + 1, 0, 0, 0, tz);
  }
  return { start: after, end: new Date(after.getTime() + 24 * 60 * 60 * 1000) };
}

/**
 * Business time elapsed between two instants. If schedule is
 * disabled, returns simple wall-clock difference.
 */
export function businessMsBetween(
  from: Date,
  to: Date,
  schedule: BusinessSchedule | undefined,
): number {
  if (!schedule || !schedule.enabled) {
    return Math.max(0, to.getTime() - from.getTime());
  }
  if (from.getTime() >= to.getTime()) return 0;

  let total = 0;
  let cursor = from;
  for (let safety = 0; safety < 1000 && cursor.getTime() < to.getTime(); safety++) {
    const window = nextOrCurrentWindow(cursor, schedule);
    if (window.start.getTime() >= to.getTime()) break;
    if (cursor.getTime() < window.start.getTime()) cursor = window.start;
    const chunkEnd = Math.min(window.end.getTime(), to.getTime());
    if (chunkEnd > cursor.getTime()) {
      total += chunkEnd - cursor.getTime();
    }
    cursor = new Date(window.end.getTime());
  }
  return total;
}

/**
 * Format a duration in milliseconds as a short SLA-countdown label.
 *
 *   85_500_000 → "23h 45m"
 *      720_000 → "12m"
 *           0 → "0m"
 *      -60_000 → "1m" (caller decides how to phrase past breaches)
 */
export function formatSlaDuration(ms: number): string {
  const abs = Math.abs(ms);
  const totalMin = Math.floor(abs / 60_000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
